// Paste-recipients flow for the Invitations page — the 2-step "add a bunch of guests at once":
//   1) paste a blob of emails or "Name <email>" (newline / comma / tab / semicolon separated)
//   2) review the parsed list + set a max RSVP group size (household cap) per row, then add them.
// Adding hands the whole list to the server (`EventGuestService.startImport`, ROADMAP #132) and closes: the server
// adds the guests in the background, and the screen shows the import's progress and report (`GuestImportStatus`) —
// the composer selects the new guests when it finishes. Uses AppDrawer (the app's panel pattern), md inputs, and a
// responsive card list (not a table) so it works on mobile.
//
// ROADMAP A2b #62 P6c — the wizard's guest list uses this same drawer with `allowContacts`: step 1 also
// picks people from the ADDRESS BOOK (the shared PersonPicker, create-in-place included), and they join
// the review list as rows of their own. Every row, pasted or picked, gets a GROUP SIZE (1 = just them,
// 2 = a couple, 5 = a family) and, from 2 up, a GROUP NAME — who the invitation is addressed to. The
// server turns a named group of 2+ into a household in the address book (P2b, `inviteHousehold.ts`).
// ⚠ One drawer for both screens, so the review list and the import payload cannot drift apart; the
// rows and the payload are built by `utils/inviteRows.ts`, which the guard runs.
//
// #62 P6c Phase B — contacts are picked as FAMILIES (`FamilyPicker`: search + tags, households with their members, a
// tick per adult who receives the invitation). A family is ONE row; its other ticked adults ride along and get their
// own links to it. The review asks the server what each row WILL do (`planImport`, a dry run) — a family member of
// someone already invited joins that invitation rather than starting a second one, and the review says so.
// ⚠ #132 — the review asks about the FIRST `GUEST_IMPORT_PLAN_MAX_ROWS` rows only (one request; the server refuses more).
//   Their plan is exact — a row can only depend on rows before it; the rows past it are still added, just not previewed.
import { useEffect, useMemo, useRef, useState } from "react";
import { ActionIcon, Box, Button, Group, NumberInput, Paper, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import { AppDrawer, AppIcon } from "@/components/generic";
import {
  BulkJob,
  EventGuestService,
  GUEST_IMPORT_PLAN_MAX_ROWS,
  ImportGuestPlanItem,
  ImportGuestRecipient,
  ImportGuestsResult,
} from "@/utils/api";
import { formatErrors, getFormErrors } from "@/utils";
import { useFormSnapshot } from "@/hooks";
import {
  InviteRow,
  PickedContact,
  buildInviteRows,
  carryReviewEdits,
  inviteRowIsExisting,
  inviteRowNeedsName,
  inviteRowSize,
  toImportRecipient,
} from "@/utils/inviteRows";
import { FamilyPicker } from "@/components/features/EventGuests/FamilyPicker";
import { isValidPhone, toE164, formatPhone } from "@em/utils";

// The import refuses the pasted list under `recipients` ("Add at least one recipient"),
// which is the one name with a control here — it renders on the paste box AND above the
// review list. `eventId` has no control, so it is left off and reaches the footer named.
const KNOWN_FORM_FIELDS = ["recipients"] as const;

const EMAIL_RE = /[^\s<>,;:"']+@[^\s<>,;:"']+\.[^\s<>,;:"']+/g;
// A phone CANDIDATE — a run of digits with the usual separators (+, spaces, (), -, .). Kept loose;
// each candidate is then validated with libphonenumber (isValidPhone) so false positives like ZIP+4
// or dates are discarded. Only genuine, possible numbers survive.
const PHONE_CANDIDATE_RE = /\+?\d[\d\s().-]{5,}\d/g;

/**
 * Parse pasted text into recipients. Chunks split on newline / semicolon / tab (NOT comma, since
 * "Name, email" uses a comma). A single-contact chunk may carry an email AND/OR a phone plus a raw
 * GROUP LABEL ("Damons Family", "John & Anna", "Mr & Mrs Smith") — we don't split the label into a
 * first/last name, because it may describe a group rather than a person (the real attendee names are
 * collected on the RSVP form). Phones are recognized in any format and normalized to E.164; an
 * unrecognizable "phone" is simply ignored. A chunk with several bare emails (a comma-list) yields one
 * nameless recipient each. Deduped by email, else by phone.
 *
 * @param text - The pasted blob.
 * @param defaultRegion - ISO alpha-2 parse region for local numbers (the brand default).
 */

export const parseRecipients = (text: string, defaultRegion: string = "US"): ImportGuestRecipient[] => {
  const out: ImportGuestRecipient[] = [];
  const seenEmail = new Set<string>();
  const seenPhone = new Set<string>();

  for (const chunk of text.split(/[\n;\t]+/).map((c) => c.trim()).filter(Boolean)) {
    const emails = chunk.match(EMAIL_RE) || [];

    // Several bare emails on one line (a comma-separated list) → one nameless recipient each.
    if (emails.length > 1) {
      for (const e of emails) {
        const email = e.toLowerCase();
        if (!seenEmail.has(email)) {
          seenEmail.add(email);
          out.push({ email, groupName: null });
        }
      }
      continue;
    }

    const email = emails[0] ? emails[0].toLowerCase() : null;

    // Look for a phone in the chunk with the email removed (so an email's digits aren't mistaken
    // for a number). Keep the first candidate that validates as a real, possible number.
    const rest = emails[0] ? chunk.replace(emails[0], " ") : chunk;
    let phone: string | null = null;
    let phoneMatch: string | null = null;
    for (const cand of rest.match(PHONE_CANDIDATE_RE) || []) {
      if (isValidPhone(cand, defaultRegion as never)) {
        phone = toE164(cand, defaultRegion as never);
        phoneMatch = cand;
        break;
      }
    }

    if (!email && !phone) {
      continue;
    }

    // Label = the chunk minus the email + the matched phone, cleaned.
    let label = chunk;
    if (emails[0]) {
      label = label.replace(emails[0], " ");
    }
    if (phoneMatch) {
      label = label.replace(phoneMatch, " ");
    }
    label = label.replace(/[<>"]/g, "").replace(/[,;]/g, " ").replace(/\s+/g, " ").trim();

    // Dedupe by email when present, else by the E.164 phone.
    if (email) {
      if (seenEmail.has(email)) {
        continue;
      }
      seenEmail.add(email);
    } else if (phone) {
      if (seenPhone.has(phone)) {
        continue;
      }
      seenPhone.add(phone);
    }

    out.push({
      email: email || undefined,
      phone: phone || undefined,
      groupName: label || null,
    });
  }

  return out;
};

export function PasteRecipientsModal({
  opened,
  onClose,
  eventId,
  onStarted,
  defaultCountry = "US",
  allowContacts = false,
  excludePersonIds = [],
  title = "Paste recipients",
  submitLabel = (count: number) => `Add ${count} & select`,
  defaultGroupSize = 1,
  existingGuests,
}: {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /**
   * #132 — the import is stored and runs on the server; the drawer has closed itself. The screen tracks the job
   * (`useBulkJobs().track`) to show its progress and act when it finishes.
   */
  onStarted: (job: BulkJob) => void;
  /** Parse region for pasted local phone numbers — the brand's space.default_country (fallback US). */
  defaultCountry?: string;
  /** #62 P6c — also pick people from the address book on the first step (the wizard's guest list). */
  allowContacts?: boolean;
  /** Contacts already on the event — shown as "already added" in the picker. */
  excludePersonIds?: string[];
  title?: string;
  /** The Add button's label for `count` rows. */
  submitLabel?: (count: number) => string;
  /**
   * What every row starts at. ⚠ The wizard passes the EVENT's own group size: a guest's size OVERRIDES the event's on
   * the reply (`PublicRsvpCore`), so starting at 1 would quietly switch off the plus-ones the host just allowed.
   */
  defaultGroupSize?: number;
  /** Who is already on the guest list (loaded pages) — their rows say that adding them again UPDATES them. */
  existingGuests?: { personIds: string[]; emails: string[]; phones: string[] };
}) {
  const [step, setStep] = useState<"paste" | "review">("paste");
  const [text, setText] = useState("");
  const [picked, setPicked] = useState<PickedContact[]>([]);
  const [rows, setRows] = useState<InviteRow[]>([]);
  const [defaultSize, setDefaultSize] = useState<number | string>(defaultGroupSize);
  // Pasted text that yielded nothing, while contacts were picked — said, not silently dropped.
  const [pasteFoundNothing, setPasteFoundNothing] = useState(false);
  // #62 P6c Phase B — what the server WILL do with each row (by row key). ⓘ Fail-soft: without it the review falls back
  // to what the page knows ("already on the guest list"), and the import itself decides the same way regardless.
  const [planByKey, setPlanByKey] = useState<Record<string, ImportGuestPlanItem>>({});
  const [planning, setPlanning] = useState(false);
  // ⚠ Only the NEWEST plan may land: Back → edit → Review again re-uses row keys, so an older answer arriving last would
  //   put its outcomes on the new rows (and switch Add on before the current plan is back).
  const planCallRef = useRef(0);
  // The rows as the last Review BUILT them, before any edit on the review screen — what `doParse` compares against.
  const builtRowsRef = useRef<InviteRow[]>([]);

  // The pasted text, the picked contacts, the default size and the reviewed rows — Esc / Cancel / Back-out ask
  // before discarding them (#123). `step` is not work: the review is reached only from a non-empty paste step.
  const { isDirty, seedFrom, clear } = useFormSnapshot({ text, picked, defaultSize, rows });

  // Each opening starts from the event's size as it is NOW (the host may have changed plus-ones since).
  useEffect(() => {
    if (opened) {
      setDefaultSize(defaultGroupSize);
      // Seeded from what is on screen — `close()` normally resets the rest, but a caller that drops `opened` itself
      // leaves it standing, and that is what the host sees on the next opening.
      seedFrom({ text, picked, defaultSize: defaultGroupSize, rows });
    } else {
      clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, defaultGroupSize]);

  const existing = useMemo(
    () => ({
      personIds: new Set(existingGuests?.personIds ?? []),
      emails: new Set((existingGuests?.emails ?? []).map((e) => e.toLowerCase())),
      phones: new Set(existingGuests?.phones ?? []),
    }),
    [existingGuests],
  );
  const [importing, setImporting] = useState(false);
  // The import's OWN result. Until now a refusal only ever became a red toast that
  // named no field and left the reviewed list behind it.
  const [submitError, setSubmitError] = useState<OpResult | null>(null);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError]
  );

  const reset = () => {
    setStep("paste");
    setText("");
    setPicked([]);
    setRows([]);
    builtRowsRef.current = [];
    setDefaultSize(defaultGroupSize);
    setPasteFoundNothing(false);
    planCallRef.current += 1;
    setPlanByKey({});
    setPlanning(false);
    setSubmitError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  /** A family (or one contact) from the address book joins the list as ONE invitation row. */
  const pickContact = (pick: PickedContact) => {
    setPicked((prev) => (prev.some((p) => p.personId === pick.personId) ? prev : [...prev, pick]));
  };

  const unpickContact = (personId: string) => {
    setPicked((prev) => prev.filter((p) => p.personId !== personId));
  };

  /** Everyone already picked here — leads and the adults riding along — so the picker shows their families as added. */
  const pickedPersonIds = useMemo(() => picked.flatMap((p) => [p.personId, ...(p.alsoNotify ?? []).map((a) => a.personId)]), [picked]);

  /** Ask the server what each row will do. Rows carry their key, so removing one later keeps the others' notes. */
  const planRows = async (next: InviteRow[]) => {
    const call = ++planCallRef.current;

    setPlanByKey({});

    if (next.length === 0) {
      setPlanning(false);

      return;
    }

    setPlanning(true);
    // ⚠ The first rows only — the plan of a row depends on EARLIER rows, so theirs is exact (#132).
    const res = await EventGuestService.planImport({ eventId, recipients: next.slice(0, GUEST_IMPORT_PLAN_MAX_ROWS).map(toImportRecipient) });

    if (call !== planCallRef.current) {
      return;
    }

    setPlanning(false);

    if (res.code !== OP_RESULT_CODES.OK) {
      return;
    }

    const plan = ((res.getDataFirst?.() ?? (res as any).data?.[0]) as ImportGuestsResult | undefined)?.plan ?? [];
    const byKey: Record<string, ImportGuestPlanItem> = {};

    for (const item of plan) {
      const row = item?.index != null ? next[item.index] : undefined;

      if (row) {
        byKey[row.key] = item;
      }
    }

    setPlanByKey(byKey);
  };

  const doParse = () => {
    setSubmitError(null);

    const parsed = text.trim() ? parseRecipients(text, defaultCountry) : [];
    const size = typeof defaultSize === "number" ? defaultSize : 1;
    setPasteFoundNothing(!!text.trim() && parsed.length === 0);
    // Back → Review again re-reads the paste; a size or a name already set on the review screen stays with its person.
    const built = buildInviteRows(picked, parsed, size);
    const next = carryReviewEdits(built, builtRowsRef.current, rows);
    builtRowsRef.current = built;
    setRows(next);
    setStep("review");
    void planRows(next);
  };

  const applyDefaultToAll = (size: number) => {
    setDefaultSize(size);
    setRows((prev) => prev.map((r) => ({ ...r, rsvpGroupSize: size })));
  };

  const setRowSize = (key: string, size: number) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, rsvpGroupSize: size } : r)));
  };

  const setRowLabel = (key: string, groupName: string) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, groupName } : r)));
  };

  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  };

  const doImport = async () => {
    if (rows.length === 0) {
      return;
    }

    setImporting(true);
    setSubmitError(null);
    // #132 — the WHOLE list goes to the server in one request and is added in the background; this answers as soon as
    // it is stored, whatever its length.
    const result = await EventGuestService.startImport({
      eventId,
      recipients: rows.map(toImportRecipient),
    });
    setImporting(false);

    if (result.code !== OP_RESULT_CODES.OK) {
      // Field-keyed refusals land on the control; the rest reach the drawer's own
      // alert right above the Add button. The drawer stays open either way.
      setSubmitError(result);

      return;
    }

    const job = (result.getDataFirst?.() ?? (result as any).data?.[0]) as BulkJob | undefined;

    if (!job?.id) {
      setSubmitError(OpResult.fail(OP_RESULT_CODES.EXCEPTION, [], "The guest list could not be saved. Please try again."));

      return;
    }

    // ⓘ No toast: the screen's import card says it is adding them, then how it went.
    reset();
    onStarted(job);
  };

  // Total expected attendees = the sum of every recipient's max group size (each invite can bring up
  // to that many people), not just the number of email rows.
  // ⚠ Phase B — a row that JOINS a family's invitation adds only what it GROWS that invitation by (the server keeps the
  //   bigger of the two sizes); the family is already counted at its own.
  const totalGuests = useMemo(
    () =>
      rows.reduce((sum, r) => {
        const item = planByKey[r.key];

        return sum + (item?.outcome === "attach" ? Math.max(0, inviteRowSize(r) - (item.leadSize ?? inviteRowSize(r))) : inviteRowSize(r));
      }, 0),
    [rows, planByKey],
  );

  const isPaste = step === "paste";

  return (
    <AppDrawer
      opened={opened}
      onClose={close}
      title={title}
      size="lg"
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType={isPaste ? "confirm-cancel" : "custom"}
      onConfirm={isPaste ? doParse : undefined}
      confirmLabel="Review"
      confirmIcon="ArrowRight"
      confirmDisabled={!text.trim() && picked.length === 0}
      cancelLabel="Cancel"
      isDirty={isDirty}
      customActions={
        isPaste ? undefined : (
          <Group justify="space-between" w="100%" wrap="nowrap">
            <Button
              variant="default"
              leftSection={<AppIcon iconName="ArrowLeft" size={16} />}
              onClick={() => setStep("paste")}
            >
              Back
            </Button>
            <Button
              leftSection={<AppIcon iconName="UserPlus" size={16} />}
              onClick={doImport}
              loading={importing}
              // ⚠ Not while the review is still asking the server what each row will do — the host would add rows the
              //   screen has not yet said will join a family's invitation.
              disabled={rows.length === 0 || planning}
            >
              {submitLabel(rows.length)}
            </Button>
          </Group>
        )
      }
    >
      {isPaste ? (
        <Stack gap="md">
          <Text size="md" c="dimmed">
            Paste emails and/or phone numbers, optionally with a name/label like{" "}
            <code>Damons Family &lt;family@example.com&gt;</code> — one per line, or separated by tabs or semicolons.
            Phones in any format are recognized; a label like &ldquo;John &amp; Anna&rdquo; is kept as the group
            name, and guests fill in their actual names when they RSVP.
          </Text>
          <Textarea
            size="md"
            label={allowContacts ? "Paste emails or phone numbers" : undefined}
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            placeholder={"Damons Family <family@example.com>\nalex@example.com\nJordan Lee, +1 403 714 0674"}
            autosize
            minRows={allowContacts ? 4 : 6}
            maxRows={16}
            autoFocus
            error={fieldErrors.recipients}
          />
          {allowContacts ? (
            <Stack gap="xs">
              <FamilyPicker excludePersonIds={excludePersonIds} pickedPersonIds={pickedPersonIds} onPick={pickContact} />
              {picked.map((p) => (
                <Paper key={p.personId} withBorder radius="md" p="xs">
                  <Group justify="space-between" wrap="nowrap" gap="sm">
                    <Box style={{ minWidth: 0 }}>
                      <Text size="md" fw={600} lineClamp={1}>
                        {p.name}
                      </Text>
                      {p.contactLine ? (
                        <Text size="md" c="dimmed" lineClamp={1}>
                          {p.contactLine}
                        </Text>
                      ) : null}
                      {p.alsoNotify && p.alsoNotify.length > 0 ? (
                        <Text size="md" c="dimmed" lineClamp={2}>
                          Also invited: {p.alsoNotify.map((a) => a.name).join(", ")}
                        </Text>
                      ) : null}
                    </Box>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="lg"
                      onClick={() => unpickContact(p.personId)}
                      aria-label={`Remove ${p.name}`}
                    >
                      <AppIcon iconName="X" size={18} />
                    </ActionIcon>
                  </Group>
                </Paper>
              ))}
            </Stack>
          ) : null}
          <NumberInput
            size="md"
            label="Default group size"
            description="How many people each invitation covers — 2 for a couple, 5 for a family. You can change each one next."
            value={defaultSize}
            onChange={(v) => setDefaultSize(typeof v === "number" ? v : 1)}
            min={1}
            max={50}
            w={240}
          />
        </Stack>
      ) : (
        <Stack gap="sm">
          {/* Summary bar: what's being added (records + expected heads) and a set-all control. */}
          <Paper withBorder radius="md" p="sm">
            <Group justify="space-between" wrap="wrap" gap="md" align="center">
              <Group gap="xs" align="baseline" wrap="wrap">
                <Text fw={700} fz="lg">
                  {rows.length}
                </Text>
                <Text size="md" c="dimmed">recipient{rows.length === 1 ? "" : "s"}</Text>
                <Text size="md" c="dimmed">·</Text>
                <Text fw={700} fz="lg">
                  {totalGuests}
                </Text>
                <Text size="md" c="dimmed">guest{totalGuests === 1 ? "" : "s"} expected</Text>
              </Group>
              <Group gap="xs" wrap="nowrap" align="center">
                <Text size="md" c="dimmed">Set all to</Text>
                <NumberInput
                  size="md"
                  aria-label="Set all group sizes"
                  value={defaultSize}
                  onChange={(v) => applyDefaultToAll(typeof v === "number" ? v : 1)}
                  min={1}
                  max={50}
                  w={96}
                />
              </Group>
            </Group>
          </Paper>

          {/* The Add button lives on THIS step, so the list's own refusal has to be
              readable here too — not only back on the paste box. */}
          {fieldErrors.recipients && <Text size="md" c="red.7">{fieldErrors.recipients}</Text>}

          {pasteFoundNothing ? (
            <Text size="md" c="orange.8">
              Nothing in the pasted text looked like an email or a phone number, so only the contacts you picked are
              below. Go back to check the text.
            </Text>
          ) : null}

          {rows.length > 0 ? (
            <Text size="md" c="dimmed">
              Give a group of 2 or more a name — who the invitation is addressed to, like &ldquo;John &amp; Anna&rdquo;.
              A named group is kept as a household in your contacts.
            </Text>
          ) : null}

          {planning ? (
            <Text size="md" c="dimmed">
              Checking who is already on the guest list…
            </Text>
          ) : null}

          {rows.length > GUEST_IMPORT_PLAN_MAX_ROWS ? (
            <Text size="md" c="dimmed">
              The first {GUEST_IMPORT_PLAN_MAX_ROWS} are checked here. The other {rows.length - GUEST_IMPORT_PLAN_MAX_ROWS} are checked as they
              are added — someone already on the guest list is updated, not added twice.
            </Text>
          ) : null}

          {rows.length === 0 ? (
            <Text size="md" c="dimmed" ta="center" py="md">
              No valid emails or phone numbers found. Go back and check your list.
            </Text>
          ) : (
            <Stack gap="xs">
              {rows.map((r) => {
                const isGroup = inviteRowSize(r) > 1;
                // A row that joins a family's invitation grows it rather than starting another (the server's rule).
                const joins = planByKey[r.key]?.outcome === "attach";
                // A picked contact is named by the address book; a pasted line only by what was pasted.
                const second = r.personId
                  ? [inviteRowNeedsName(r) ? r.contactName : null, r.contactLine].filter(Boolean).join(" · ")
                  : [r.email, r.phone ? formatPhone(r.phone) : null].filter(Boolean).join(" · ");

                return (
                  <Paper key={r.key} withBorder radius="md" p="sm">
                    <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-end">
                      {inviteRowNeedsName(r) ? (
                        <TextInput
                          size="md"
                          style={{ flex: 1, minWidth: 0 }}
                          label={isGroup ? "Group name" : "Name or group"}
                          placeholder={isGroup ? "e.g. John & Anna" : "e.g. Damons Family"}
                          value={r.groupName ?? ""}
                          onChange={(e) => setRowLabel(r.key, e.currentTarget.value)}
                        />
                      ) : (
                        <Box style={{ flex: 1, minWidth: 0 }} pb={6}>
                          <Text size="md" fw={600} lineClamp={1}>
                            {r.contactName}
                          </Text>
                        </Box>
                      )}
                      <NumberInput
                        size="md"
                        label="Group size"
                        value={inviteRowSize(r)}
                        onChange={(v) => setRowSize(r.key, typeof v === "number" ? v : 1)}
                        min={1}
                        max={50}
                        w={112}
                      />
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="lg"
                        mb={4}
                        onClick={() => removeRow(r.key)}
                        aria-label="Remove"
                      >
                        <AppIcon iconName="X" size={18} />
                      </ActionIcon>
                    </Group>
                    {second ? (
                      <Text size="md" c="dimmed" mt={6}>
                        {second}
                      </Text>
                    ) : null}
                    {r.alsoNotifyNames && r.alsoNotifyNames.length > 0 ? (
                      <Text size="md" c="dimmed" mt={4}>
                        Also invited: {r.alsoNotifyNames.join(", ")} — each gets their own link to this one invitation when you send it, and the family is counted once.
                        {planByKey[r.key] && (planByKey[r.key].linked ?? 0) < r.alsoNotifyNames.length
                          ? ` ${r.alsoNotifyNames.length - (planByKey[r.key].linked ?? 0)} of them ${
                              r.alsoNotifyNames.length - (planByKey[r.key].linked ?? 0) === 1 ? "is" : "are"
                            } already on the guest list.`
                          : ""}
                      </Text>
                    ) : null}
                    {joins ? (
                      <Text size="md" c="blue.8" mt={4}>
                        Joins the invitation for {planByKey[r.key].leadName || "their family"}
                        {planByKey[r.key].eventGuestId ? ", already on the guest list" : ", added above"} — no second invitation. They get
                        their own link to it the next time you send that invitation. It grows to this group size if that is bigger, and
                        takes this name only if it has none.
                      </Text>
                    ) : planByKey[r.key]?.outcome === "match" || (!planByKey[r.key] && inviteRowIsExisting(r, existing)) ? (
                      <Text size="md" c="orange.8" mt={4}>
                        Already on the guest list — adding them again updates their group size, and their group&rsquo;s name if you give one.
                      </Text>
                    ) : null}
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>
      )}
    </AppDrawer>
  );
}
